'use client';

import * as React from 'react';
import { add, format } from 'date-fns';
import { IconCalendar } from 'icon/Time/calendar';
import { cn } from '../../utils';
import { Button } from '../button';
import { Calendar } from '../calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../popover';
import { TimePicker } from './time-picker';

export type DateTimePickerProps = {
  value: Date | undefined;
  onChange: (date: Date | undefined) => void;
  fromDate?: Date;
  onPopoverOpenChange?: (open: boolean) => void;
};

export function DateTimePicker({
  value,
  onChange,
  fromDate,
  onPopoverOpenChange,
}: DateTimePickerProps) {
  const initialDate = React.useMemo(() => new Date(), []);
  /**
   * carry over the current time when a user clicks a new day
   * instead of resetting to 00:00
   */
  const handleSelect = (newDay: Date | undefined) => {
    if (!newDay) {
      return;
    }
    const prev = value ?? initialDate;
    const diff = newDay.getTime() - prev.getTime();
    const diffInDays = diff / (1000 * 60 * 60 * 24);
    const newDateFull = add(prev, { days: Math.ceil(diffInDays) });
    onChange(newDateFull);
  };

  return (
    <Popover modal onOpenChange={onPopoverOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            'w-full justify-start text-left',
            !value && 'text-tertiary',
          )}
        >
          <IconCalendar className="mr-1.5 size-5" />
          {value ? format(value, 'PPP HH:mm:ss') : <span>Pick a date</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0">
        <Calendar
          mode="single"
          selected={value ?? initialDate}
          onSelect={(d) => handleSelect(d)}
          initialFocus
          fromDate={fromDate}
        />
        <div className="border-t p-3">
          <TimePicker setDate={onChange} date={value ?? initialDate} />
        </div>
      </PopoverContent>
    </Popover>
  );
}
